import { isBefore } from 'date-fns'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { FilterToggle, FiltersSubmit, ApplyButtonWrapper } from 'components/Filters'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import Dropdown from 'components/v2/Dropdown'
import { InputDate } from 'components/v2/InputDate'
import { Label } from 'components/v2/Label'
import { Row } from 'components/v2/Row'
import { IRoutesPlanningFilters, IRoutesPlanningFiltersModel } from 'features/RoutesPlanning/models'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { RouteAutocomplete } from 'features/Routes/components/RouteAutocomplete'
import { mapFormToQueryString } from 'features/RoutesPlanning/views/Index/SecondaryFilters/utils'
import { getDifference } from 'utils/object'
import { device } from 'utils/style'

const messages = defineMessages({
  selectRoute: {
    id: 'routesPlanning.filters.selectRoute',
    defaultMessage: 'Select route'
  },
  selectBusinessSite: {
    id: 'routesPlanning.filters.selectBusinessSite',
    defaultMessage: 'Select business site'
  },
  datePlaceholder: {
    id: 'routesPlanning.filters.datePlaceholder',
    defaultMessage: '--/--/----'
  }
})

const DropdownWrapper = styled.div`
  padding: 24px;
`

const FiltersContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, 100%);

  @media ${device.tablet} {
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  }
`

interface IRoutePlanningFiltersProps {
  filters: IRoutesPlanningFiltersModel
  businessClientId: string
  companyId: string
}

export const RoutesPlanningSecondaryFilters = ({
  filters,
  businessClientId,
  companyId
}: IRoutePlanningFiltersProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const filterRoutesPlanning = (values: IRoutesPlanningFilters): void => {
    navigate(`/routes-planning?${mapFormToQueryString({ companyId, businessClientId, formValues: values })}`)
    setIsDropdownOpen(false)
  }

  const defaultValues: IRoutesPlanningFilters = {
    businessSite: {
      id: '',
      name: ''
    },
    routes: [],
    offerDateRange: {
      startAt: null,
      endAt: null
    }
  }

  const initialValues: IRoutesPlanningFilters = {
    businessSite: filters.businessSite || defaultValues.businessSite,
    routes: filters.routes || defaultValues.routes,
    offerDateRange: filters.offerDateRange || defaultValues.offerDateRange
  }

  const handleSubmit = (): void => {
    // Do nothing, only apply filters when aply button is pressed
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, setFieldValue, setValues, values } = props

        const handleResetFilters = (): void => {
          setValues(defaultValues)
          filterRoutesPlanning(defaultValues)
        }

        const handleStartAtChange = (startAt?: Date): void => {
          const dateRange = values.offerDateRange

          setValues({
            ...values,
            offerDateRange: {
              startAt: startAt || null,
              endAt: dateRange.endAt && startAt && isBefore(dateRange.endAt, startAt) ? startAt : dateRange.endAt
            }
          })
        }

        const handleEndAtChange = (endAt?: Date): void => {
          const dateRange = values.offerDateRange

          setValues({
            ...values,
            offerDateRange: {
              startAt: dateRange.startAt && endAt && isBefore(endAt, dateRange.startAt) ? endAt : dateRange.startAt,
              endAt: endAt || null
            }
          })
        }

        const hasPendingFilters = !isEmpty(getDifference(initialValues, values))

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <Dropdown.Wrapper onClickAway={(): void => setIsDropdownOpen(false)}>
                <FilterToggle onClick={(): void => setIsDropdownOpen(!isDropdownOpen)}>
                  <FormattedMessage id="routePlanning.filters.more" defaultMessage="More Filters" />
                </FilterToggle>
                <Dropdown.Content open={isDropdownOpen} noIndicator noOverflow right>
                  <DropdownWrapper>
                    <FiltersContainer>
                      <BusinessSiteAutocomplete
                        id="businessSite"
                        placeholder={intl.formatMessage(messages.selectBusinessSite)}
                        value={values.businessSite.name}
                        label={<FormattedMessage id="dictionary.businessSite" defaultMessage="Business site" />}
                        onChange={(businessSite): void => {
                          setFieldValue('businessSite', businessSite)
                        }}
                        businessClientId={businessClientId}
                      />
                      <RouteAutocomplete
                        placeholder={intl.formatMessage(messages.selectRoute)}
                        value={values.routes.at(0)?.name || ''}
                        label={<FormattedMessage id="dictionary.route" defaultMessage="Route" />}
                        onChange={(routes): void => {
                          setFieldValue('routes', routes)
                        }}
                        businessClientIds={businessClientId ? [businessClientId] : []}
                        businessSiteIds={values.businessSite.id ? [values.businessSite.id] : []}
                        uniqueName
                      />
                      <div>
                        <Label>
                          <FormattedMessage id="dictionary.offerDate" defaultMessage="Offer date" />
                        </Label>
                        <Row>
                          <Column>
                            <InputDate
                              intl={intl}
                              value={values.offerDateRange.startAt || ''}
                              onChange={handleStartAtChange}
                              placeholder={intl.formatMessage(messages.datePlaceholder)}
                              optional
                              left
                            />
                          </Column>
                          <Column>
                            <InputDate
                              intl={intl}
                              value={values.offerDateRange.endAt || ''}
                              onChange={handleEndAtChange}
                              placeholder={intl.formatMessage(messages.datePlaceholder)}
                              optional
                              right
                            />
                          </Column>
                        </Row>
                      </div>
                      <ApplyButtonWrapper>
                        <Button type="button" kind="cancel" onClick={handleResetFilters}>
                          <FormattedMessage id="dictionary.reset" defaultMessage="Reset" />
                        </Button>
                        <Button
                          kind="primary"
                          type="button"
                          disabled={!hasPendingFilters}
                          onClick={(): void => filterRoutesPlanning(values)}
                        >
                          <FormattedMessage id="dictionary.apply" defaultMessage="Apply" />
                        </Button>
                      </ApplyButtonWrapper>
                    </FiltersContainer>
                  </DropdownWrapper>
                </Dropdown.Content>
              </Dropdown.Wrapper>
              <FiltersSubmit />
            </div>
          </form>
        )
      }}
    </Formik>
  )
}
